<template>
  <div>
    <v-row v-if="activity">
      <v-col>
        <p class="font-weight-bold">Please click to review the following agreement:</p>
        <v-list
          outlined
          :dark="viewed"
          :color="viewed ? 'primary' : 'warning'"
        >
          <v-list-item-group>
            <v-list-item
              @click="open"
            >
              <v-list-item-icon>
                <v-icon v-if="viewed">done</v-icon>
                <v-icon v-else>priority_high</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="font-weight-bold">{{ memberTypeName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p>
          This agreement is made between:
        </p>
        <p>
          MEAT AND LIVESTOCK AUSTRALIA LIMITED ABN 39 081 678 364 of Level 1, 40 Mount Street, North Sydney, NSW (PO Box 1961, North Sydney, NSW 2059) (MLA)
        </p>
        <p>
          and
        </p>
        <p>
          {{ registration.entityName }} (You)<br/>
          ABN {{ registration.abn | mask('## ### ### ###') }}
        </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p class="font-weight-black text-uppercase">
          Executed as an agreement
        </p>
        <v-form>
          <v-text-field
            label="Name of authorised Person"
            v-model="nameOfAuthorisedPerson"
            :disabled="accepted"
          />
          <v-text-field
            label="Office held"
            v-model="officeHeld"
            :disabled="accepted"
          />
          <p>
            Agreed and accepted on behalf of the applicant named above by clicking the acceptance button below.
          </p>
          <v-btn
            color="primary"
            :disabled="!allowSave"
            :loading="saving"
            @click="save"
          >
            Click to accept as at {{ date | formatDate }}
          </v-btn>
        </v-form>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      max-width="1000"
      fullscreen
      scrollable
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Agreement for {{ memberTypeName }}
        </v-card-title>

        <v-card-text class="pa-0 d-flex">
          <v-row
            v-show="!frameLoaded"
            align="center"
            justify="center"
          >
            <v-col class="text-center">
              <p>If the agreement fails to load, please click the link below.</p>
              <a
                :href="agreementUrl"
                target="_blank"
                @click="frameLoaded = true"
              >
                {{ agreementUrl }}
              </a>
            </v-col>
          </v-row>

          <iframe
            width="100%"
            allowfullscreen="false"
            referrerpolicy="no-referrer-when-downgrade"
            :src="agreementUrl"
            v-show="frameLoaded"
            @load="onFrameLoad"
          />
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            v-if="frameLoaded"
            color="primary"
            text
            @click="accept"
          >
            I accept
          </v-btn>
          <span v-else>Please review the license agreement</span>
          <v-spacer />
          <v-btn
            text
            @click="dialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'

import { LICENSE_AGREEMENT_GENERIC } from '../utilities/env.js'

export default {
  name: 'RegistrationAgreement',

  props: {
    registrationId: {
      type: String,
      required: true
    },
    activityId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    LICENSE_AGREEMENT_GENERIC,
    registration: {
      activities: []
    },
    dialog: false,
    frameLoaded: false,
    viewed: false,
    date: new Date(),
    nameOfAuthorisedPerson: '',
    officeHeld: '',
    saving: false,
    accepted: false
  }),

  computed: {
    allowSave () {
      return this.viewed &&
        !this.accepted &&
        this.nameOfAuthorisedPerson.trim() &&
        this.officeHeld.trim()
    },

    activity () {
      return this.registration.activities.find(o => o._id === this.activityId)
    },

    agreementUrl () {
      return this.activity?.memberType?.agreementUrl || LICENSE_AGREEMENT_GENERIC
    },

    memberTypeName () {
      return this.activity?.memberType?.name ?? ''
    }
  },

  methods: {
    onError (err) {
      this.$emit('error', err)
    },

    open () {
      this.dialog = true
    },

    accept () {
      this.viewed = true
      this.dialog = false
    },

    onFrameLoad () {
      this.frameLoaded = true
    },

    async save () {
      this.saving = true
      const licenseAgreement = {
        accepted: true,
        agreementUrl: this.agreementUrl,
        date: this.date,
        nameOfAuthorisedPerson: this.nameOfAuthorisedPerson,
        officeHeld: this.officeHeld
      }
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $activityId: MongoID!, $record: UpdateByIdRegistrationActivitiesInput!) {
            registrationUpdateActivityById(_id: $id, activityId: $activityId, record: $record) {
              _id
            }
          }`,
          variables: {
            id: this.registrationId,
            activityId: this.activityId,
            record: { licenseAgreement }
          }
        })
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.saving = false
      }
      this.accepted = true
      this.$emit('save')
    }
  },

  apollo: {
    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          entityName
          abn
          activities {
            _id
            type
            subType
            memberType {
              _id
              name
              agreementUrl
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      error (e) { this.onError(e) }
    }
  }
}
</script>
